import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Eye, Users, Calendar, Building2 } from "lucide-react";
import { api, formatINR, formatCompactNumber, formatApiErrorDetail } from "@/lib/api";
import { imageForFormat, IMG } from "@/lib/images";
import SmartImage from "@/components/common/SmartImage";

export default function InventoryDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });

  useEffect(() => {
    setLoading(true);
    api.get(`/public/inventory/${id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);

  async function submitInterest(e) {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/public/leads", {
        ...form,
        source: "marketplace_request",
        message: `Interested in ${data?.item?.format} at ${data?.item?.mall_name} (${data?.item?.city}). ${form.message}`,
      });
      toast.success("Request received. Our strategist will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <div className="pt-40 max-w-[1440px] mx-auto px-6 text-[var(--slate-2)]">Loading…</div>;
  if (!data) return (
    <div className="pt-40 max-w-[1440px] mx-auto px-6 text-[var(--slate-2)]">
      Inventory not found. <Link to="/marketplace" className="text-[var(--gold)] underline">Back to marketplace</Link>
    </div>
  );

  const { item, mall } = data;

  return (
    <div data-testid="inventory-detail" className="bg-[var(--bone)]">
      {/* Editorial banner */}
      <section className="relative h-[55vh] lg:h-[68vh] overflow-hidden">
        <SmartImage src={item.photos?.[0]} format={item.format} alt={item.format} className="absolute inset-0 w-full h-full object-cover img-luxe" />
        <div className="absolute inset-0 hero-overlay-strong" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 h-full flex flex-col justify-end pb-10 lg:pb-16">
          <Link to="/marketplace" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/85 hover:text-[var(--gold-light)] mb-8">
            <ArrowLeft size={12} /> All placements
          </Link>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--gold-light)] font-semibold">
            {item.format_category} · {item.city}
          </div>
          <h1 className="mt-4 font-serif-display text-5xl md:text-6xl lg:text-7xl text-white max-w-4xl leading-[0.95]">
            {item.format}
          </h1>
          <div className="mt-4 text-white/85 text-lg">
            <Building2 size={14} className="inline -mt-1 mr-2" /> {item.mall_name}
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          {/* Photo grid */}
          {item.photos?.length > 1 && (
            <div className="grid grid-cols-3 gap-2 mb-12">
              {item.photos.slice(1, 4).map((p, idx) => (
                <div key={idx} className="aspect-[16/10] overflow-hidden">
                  <SmartImage src={p} format={item.format} alt="" className="w-full h-full object-cover img-luxe" />
                </div>
              ))}
            </div>
          )}

          <div className="prose-editorial">
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">Overview</div>
            <p className="mt-4 font-serif text-2xl lg:text-3xl leading-[1.4] text-[var(--ink)]">
              {item.description}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)]" data-testid="inv-metrics">
            <Metric icon={Eye} label="Visibility" value={`${item.visibility_score}/100`} />
            <Metric icon={Users} label="Daily footfall" value={formatCompactNumber(item.daily_footfall || item.monthly_footfall)} />
            <Metric icon={Calendar} label="Min booking" value={`${item.min_booking_weeks} wk`} />
            <Metric icon={MapPin} label="Indoor" value={item.indoor ? "Yes" : "No"} />
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <DetailBlock label="Dimensions" value={item.dimensions} />
            <DetailBlock label="Audience" value={item.demographics} />
            <DetailBlock label="Placement" value={item.placement} />
            <DetailBlock label="Availability" value={item.availability} />
          </div>

          {mall && (
            <div className="mt-14 pt-10 border-t border-[var(--border)]">
              <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">About the mall</div>
              <h3 className="mt-3 font-serif-display text-3xl md:text-4xl text-[var(--ink)]">{mall.name}</h3>
              <p className="mt-4 text-[var(--slate-1)] text-base lg:text-lg max-w-2xl">{mall.description}</p>
              <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <span className="text-[var(--slate-2)]">Category: <span className="text-[var(--ink)] font-medium">{mall.category}</span></span>
                <span className="text-[var(--slate-2)]">Tier: <span className="text-[var(--ink)] font-medium">{mall.tier}</span></span>
                <span className="text-[var(--slate-2)]">Catchment: <span className="text-[var(--ink)] font-medium">{mall.daily_footfall ? formatCompactNumber(mall.daily_footfall) + "/day" : "—"}</span></span>
              </div>
            </div>
          )}
        </div>

        {/* Sticky sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-28 bg-[var(--obsidian)] text-[var(--bone)] p-8 lg:p-10 dark-mode">
            <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[var(--gold-light)]">Indicative pricing</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-serif-display text-5xl text-white">{formatINR(item.price_per_day)}</span>
              <span className="text-white/60 text-sm">/ day</span>
            </div>
            <p className="mt-2 text-xs text-white/55">
              Approx {formatINR(item.price_per_week)} / week. Final pricing depends on duration and creative bundle.
            </p>

            <form className="mt-7 space-y-3" onSubmit={submitInterest} data-testid="inv-lead-form">
              <input data-testid="inv-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
              <input data-testid="inv-form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email" type="email" />
              <input data-testid="inv-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" />
              <input data-testid="inv-form-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company" />
              <textarea data-testid="inv-form-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Brief about your campaign…" />
              <button type="submit" data-testid="inv-form-submit" disabled={submitting} className="btn-primary w-full justify-center !bg-[var(--gold)] !border-[var(--gold)] hover:!bg-white hover:!text-[var(--obsidian)] !text-white">
                {submitting ? "Sending…" : "Request proposal"}
              </button>
            </form>

            <div className="mt-6 text-[11px] uppercase tracking-[0.18em] text-white/45">
              Our strategist will email a tailored mall + format plan within 24 hours.
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="bg-[var(--bone)] p-5">
      <Icon size={16} className="text-[var(--gold)]" strokeWidth={1.5} />
      <div className="mt-4 font-serif-display text-2xl text-[var(--ink)]">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-[var(--slate-2)]">{label}</div>
    </div>
  );
}

function DetailBlock({ label, value }) {
  return (
    <div className="border border-[var(--border)] p-6">
      <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[var(--gold)]">{label}</div>
      <div className="mt-3 text-[var(--ink)] text-base">{value}</div>
    </div>
  );
}
